import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

interface Airport {
  iata: string;
  name: string;
  city: string;
  country: string;
}

interface AirportAutocompleteProps {
  value: string;
  onChange: (display: string, iata: string) => void;
  placeholder?: string;
  icon?: string;
  label?: string;
}

const DEFAULT_POPULAR_AIRPORTS: Airport[] = [
  { iata: 'LOS', name: 'Murtala Muhammed International', city: 'Lagos', country: 'Nigeria' },
  { iata: 'ABV', name: 'Nnamdi Azikiwe International', city: 'Abuja', country: 'Nigeria' },
  { iata: 'LHR', name: 'Heathrow Airport', city: 'London', country: 'United Kingdom' },
  { iata: 'JED', name: 'King Abdulaziz International', city: 'Jeddah', country: 'Saudi Arabia' },
  { iata: 'MED', name: 'Prince Mohammad Bin Abdulaziz', city: 'Medina', country: 'Saudi Arabia' },
  { iata: 'DXB', name: 'Dubai International', city: 'Dubai', country: 'UAE' },
  { iata: 'JFK', name: 'John F. Kennedy International', city: 'New York', country: 'USA' },
  { iata: 'CAI', name: 'Cairo International Airport', city: 'Cairo', country: 'Egypt' },
  { iata: 'IST', name: 'Istanbul Airport', city: 'Istanbul', country: 'Turkey' },
];

export default function AirportAutocomplete({
  value,
  onChange,
  placeholder = 'City or Airport',
  icon = 'ri-flight-takeoff-line',
  label
}: AirportAutocompleteProps) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState<Airport[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Sync internal state if prop changes externally
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  // Click outside to close
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Search API
  useEffect(() => {
    if (!isOpen) return;

    if (!query || query.trim().length === 0) {
      setResults(DEFAULT_POPULAR_AIRPORTS);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`/api/travel/airports?q=${encodeURIComponent(query.trim())}`);
        if (Array.isArray(res.data) && res.data.length > 0) {
          setResults(res.data);
        } else {
          // Local filter fallback
          const q = query.trim().toLowerCase();
          const filtered = DEFAULT_POPULAR_AIRPORTS.filter(
            a => a.iata.toLowerCase().includes(q) ||
                 a.city.toLowerCase().includes(q) ||
                 a.country.toLowerCase().includes(q) ||
                 a.name.toLowerCase().includes(q)
          );
          setResults(filtered);
        }
      } catch {
        const q = query.trim().toLowerCase();
        const filtered = DEFAULT_POPULAR_AIRPORTS.filter(
          a => a.iata.toLowerCase().includes(q) ||
               a.city.toLowerCase().includes(q) ||
               a.country.toLowerCase().includes(q)
        );
        setResults(filtered);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [query, isOpen]);

  const handleSelect = (airport: Airport) => {
    const displayText = `${airport.city} (${airport.iata})`;
    setQuery(displayText);
    onChange(displayText, airport.iata);
    setIsOpen(false);
  };

  const handleInputChange = (text: string) => {
    setQuery(text);
    // If user types raw 3-letter IATA code
    const trimmed = text.trim().toUpperCase();
    if (trimmed.length === 3 && /^[A-Z]{3}$/.test(trimmed)) {
      onChange(trimmed, trimmed);
    } else {
      onChange(text, trimmed);
    }
    setIsOpen(true);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      {label && (
        <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
          {label}
        </label>
      )}
      <div className="relative">
        <i className={`${icon} absolute left-3.5 top-1/2 -translate-y-1/2 text-[#b8a080] text-lg pointer-events-none z-10`}></i>
        <input
          type="text"
          value={query}
          onFocus={() => {
            setIsOpen(true);
            if (!query) setResults(DEFAULT_POPULAR_AIRPORTS);
          }}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-white/10 hover:bg-white/15 focus:bg-white/20 border border-white/20 focus:border-[#b8a080] text-white placeholder-gray-400 text-sm font-medium rounded-xl pl-10 pr-4 py-3 outline-none transition-all duration-200 shadow-inner"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              onChange('', '');
              setResults(DEFAULT_POPULAR_AIRPORTS);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white text-xs cursor-pointer"
          >
            <i className="ri-close-circle-fill text-base"></i>
          </button>
        )}
      </div>

      {/* Autocomplete Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-gray-900/95 backdrop-blur-xl border border-white/15 rounded-xl shadow-2xl overflow-hidden z-50 max-h-64 overflow-y-auto divide-y divide-white/5">
          <div className="px-3 py-1.5 bg-black/40 text-[10px] uppercase font-bold text-gray-400 tracking-wider flex items-center justify-between">
            <span>{query ? 'Matching Airports' : 'Popular Destinations'}</span>
            <span className="text-[#b8a080]">Rigland Global</span>
          </div>
          {results.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-400">
              No airports found for "{query}". You can still search with this code.
            </div>
          ) : (
            results.map((airport) => (
              <button
                key={airport.iata}
                type="button"
                onClick={() => handleSelect(airport)}
                className="w-full text-left px-4 py-2.5 hover:bg-white/10 flex items-center gap-3 transition-colors cursor-pointer group"
              >
                <div className="w-10 h-10 rounded-lg bg-[#b8a080]/15 group-hover:bg-[#b8a080]/30 border border-[#b8a080]/30 flex items-center justify-center flex-shrink-0 transition-colors">
                  <span className="font-bold text-[#b8a080] text-xs tracking-wider">{airport.iata}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-white text-sm truncate">{airport.city}</span>
                    <span className="text-[11px] text-gray-400">{airport.country}</span>
                  </div>
                  <p className="text-xs text-gray-400 truncate">{airport.name}</p>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
